import {Component, OnInit} from '@angular/core';
import {TranslateService} from "@ngx-translate/core";
import {Language} from "./models/language";
import {ConfigService} from "./service/config-service";
import {UserService} from "./service/user.service";
import {BACKEND_VERSION, FRONTEND_VERSION, NEED_TO_LOGOUT_VERSION} from "./constants/key.constants";
import {NeedToLogoutResponse} from "./interface/needToLogoutResponse";
import {VersionResponse} from "./interface/versionResponse";
import {NavigationEnd, Router} from "@angular/router";
import {LANG} from "./constants/local-storage.contant";
import {CookieUtils} from "./utils/cookie.utils";
import {filter} from "rxjs";
import {GOOGLE_TAG_MANAGER_ID} from "./constants/secret-key.constant";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {

  constructor(
    private translateService: TranslateService,
    private configService: ConfigService,
    private userService: UserService,
    private router: Router,
  ) {
    this.translateService.setDefaultLang(Language.HY);
    this.translateService.use(CookieUtils.getCookie(LANG) || Language.HY);
    this.stripCachebuster();
    this.trackPageNavigationForGoogleAnalytics();
  }

  ngOnInit(): void {
    this.checkNeedToHardReloadAndEmptyCashes();
    this.checkNeedLogOut();
  }

  private stripCachebuster(): void {
    const url = new URL(window.location.href);
    if (url.searchParams.has('cachebuster')) {
      url.searchParams.delete('cachebuster');
      window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
    }
  }

  private trackPageNavigationForGoogleAnalytics(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        (window as any).gtag('config', GOOGLE_TAG_MANAGER_ID, {
          page_path: event.urlAfterRedirects
        });
      });
  }

  private checkNeedToHardReloadAndEmptyCashes(): void {
    this.configService.getBackAndFrontVersion().subscribe(backAndFrontVersion => {
      if (backAndFrontVersion) {
        const backendVersion = this.getValueFromLocalStorageByKey(BACKEND_VERSION);
        const frontendVersion = this.getValueFromLocalStorageByKey(FRONTEND_VERSION);

        if (!backendVersion || !frontendVersion) {
          this.hardReloadAndEmptyCashes(backAndFrontVersion);
        } else if (backendVersion !== backAndFrontVersion.backEndVersion || frontendVersion != backAndFrontVersion.frontEndVersion) {
          this.hardReloadAndEmptyCashes(backAndFrontVersion);
        }
      }
    })
  }

  hardReloadAndEmptyCashes(versionResponse: VersionResponse) {
    this.setValueIntoLocalStorage(BACKEND_VERSION, versionResponse.backEndVersion);
    this.setValueIntoLocalStorage(FRONTEND_VERSION, versionResponse.frontEndVersion);
    window.location.reload();
  }

  private checkNeedLogOut(): void {
    this.configService.checkNeedToLogout().subscribe(needToLogout => {
      if (this.isNeedToLogout(needToLogout)) {
        const logoutVersion = needToLogout.logoutVersion;
        const currentUrl = this.router.url;
        this.userService.clearDates(currentUrl);
        this.setValueIntoLocalStorage(NEED_TO_LOGOUT_VERSION, logoutVersion);
      }
    });
  }

  private isNeedToLogout(needToLogout: NeedToLogoutResponse): boolean {
    const lastLogoutVersionString = this.getValueFromLocalStorageByKey(NEED_TO_LOGOUT_VERSION);
    const lastLogoutVersion = lastLogoutVersionString ? Number(lastLogoutVersionString) : undefined;
    return lastLogoutVersion === undefined
      ? true
      : lastLogoutVersion !== needToLogout.logoutVersion && needToLogout.isNeedToLogout;
  }

  private getValueFromLocalStorageByKey(key: string) {
    return localStorage.getItem(key)
  }

  private setValueIntoLocalStorage(key: any, value: any) {
    localStorage.setItem(key, String(value))
  }
}
